import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Nynäshamn?", a: "Ansökan görs till tillståndsenheten i Nynäshamns kommun. Verksamhet i annan kommun (t.ex. Haninge, Botkyrka, Södertälje) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Nynäshamn?", a: "Nynäshamns kommun tar ut en ansökningsavgift och en årlig tillsynsavgift beroende på tillståndstyp och alkoholomsättning – aktuella avgifter finns på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Nynäshamn?", a: "Räkna med 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-nynashamn")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Nynäshamn" },
      ]}
      title="Serveringstillstånd Nynäshamn – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Nynäshamn: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.nynashamn, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Var ansöker du i Nynäshamn?", body: "Tillståndsenheten i Nynäshamns kommun ansvarar för serveringstillstånd. För verksamhet i grannkommuner som Haninge, Botkyrka eller Södertälje ansöker du hos respektive kommun." },
        { h2: "Krav från Nynäshamns kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) har Nynäshamn många säsongsverksamheter i hamnen, skärgården och på Landsort. Kommunens riktlinjer för uteservering och tillfälliga tillstånd är särskilt relevanta för sommarverksamheter. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt." },
        { h2: "Kunskapsprovet i Nynäshamn", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning, nykterhet och marknadsföring enligt FoHMFS 2014:7. Träna i samma format med Alkoholprovet." },
        { h2: "Handläggningstid", body: "Räkna med 8–12 veckor från komplett ansökan till beslut. För säsongsverksamhet i skärgården – sök senast under vintern inför sommaren." },
        { h2: "Tillsyn och uppföljning", body: "Nynäshamns kommun bedriver aktiv tillsyn under sommarsäsongen tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd." },
        { h2: "Lokala särdrag – hamn, färja och skärgård", body: "Nynäshamn präglas av färjetrafik till Gotland och en aktiv skärgård med säsongskrogar och festivaler. Anpassa ansökan efter om verksamheten är helårs eller säsong och kontakta tillståndsenheten tidigt vid frågor." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-haninge", label: "Serveringstillstånd Haninge" },
          { to: "/serveringstillstand-botkyrka", label: "Serveringstillstånd Botkyrka" },
          { to: "/serveringstillstand-sodertalje", label: "Serveringstillstånd Södertälje" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Nynäshamn – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Nynäshamn: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Nynäshamn – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Nynäshamn." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-nynashamn" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-nynashamn" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Nynäshamn", url: "https://alkoholprovet.se/serveringstillstand-nynashamn" },
      ]),
    ],
  }),
});
